import { API_URL } from "@/lib/config";
import { getSessionAuth } from "@/lib/auth/session";
import { impersonationHeaders } from "@/lib/impersonation/storage";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    public readonly path: string,
    public readonly code?: string,
  ) {
    super(`${path} → HTTP ${status}${body ? `: ${body.slice(0, 300)}` : ""}`);
  }
}

/**
 * `"/api/chats"` → `${API_URL}/api/chats`. Absolute URLs pass through. Keeping
 * the `/api/...` strings means every existing SWR key and `mutate(...)` call
 * site stays as it is.
 */
export function apiUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  return `${API_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * Bearer, plus the impersonation token while an admin works in this user's
 * session: the backend needs the request itself to say so (billing is
 * read-only then), the Supabase session being the user's own.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const auth = await getSessionAuth();
  if (!auth) return {};
  return {
    Authorization: `Bearer ${auth.accessToken}`,
    ...impersonationHeaders(auth.userId),
  };
}

/** Drop-in for `fetch("/api/…", init)`: bearer added, URL mapped, JSON content-type for string bodies. */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(await authHeaders()))
    headers.set(key, value);
  if (typeof init.body === "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(apiUrl(path), { ...init, headers });
}

async function toJson<T>(response: Response, path: string): Promise<T> {
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let code: string | undefined;
    try {
      code = (JSON.parse(text) as { code?: string }).code;
    } catch {
      // not JSON
    }
    throw new ApiError(response.status, text, path, code);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const send =
  <T>(method: string) =>
  (path: string, body?: unknown, init?: RequestInit) =>
    apiFetch(path, {
      ...init,
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    }).then((response) => toJson<T>(response, path));

export const api = {
  get: <T>(path: string, init?: RequestInit) =>
    apiFetch(path, { ...init, method: "GET" }).then((response) =>
      toJson<T>(response, path),
    ),
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    send<T>("POST")(path, body, init),
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    send<T>("PATCH")(path, body, init),
  put: <T>(path: string, body?: unknown, init?: RequestInit) =>
    send<T>("PUT")(path, body, init),
  del: <T = void>(path: string, init?: RequestInit) =>
    apiFetch(path, { ...init, method: "DELETE" }).then((response) =>
      toJson<T>(response, path),
    ),
};

/** Global SWR fetcher: string keys starting with `/api/` resolve to the backend. */
export const swrFetcher = <T>(key: string) => api.get<T>(key);
